import { Link } from "react-router-dom";
import { ArrowRight, BellRing, Phone } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Motion";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";
import EditorialSources from "@/components/EditorialSources";
import PracticalGuideLinks from "@/components/PracticalGuideLinks";
import { SEO_PAGES } from "@/data/seo_pages";
import { COMPANY, telHref } from "@/data/company";
import { openCallbackModal, trackEvent } from "@/lib/analytics";
import { hy } from "@/lib/hyphenate";

const PHONE = COMPANY.phone;
const SITE = COMPANY.url;

export default function SeoLanding({ slug }) {
  const page = SEO_PAGES.find((p) => p.slug === slug);
  const pillarLinks = SEO_PAGES.filter((p) => p.slug !== page.slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: page.h1, item: `${SITE}/${page.slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div data-testid={`seo-page-${page.slug}`}>
      <Seo title={page.title} description={page.desc} path={`/${page.slug}`} jsonLd={jsonLd} />
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-14 lg:py-20">
        <Reveal y={10}>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60 mb-6">
            <Link to="/" className="hover:text-accent">Accueil</Link>
            <span className="mx-2 text-accent">/</span>
            <span className="text-ink">{page.tag}</span>
          </p>
          <SectionTag className="mb-4">RC Pro + Décennale — Devis gratuit — Spécialistes BTP</SectionTag>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.92] mb-6 max-w-5xl">
            {hy(page.h1)}
          </h1>
          <p className="max-w-2xl text-base sm:text-lg leading-relaxed text-ink/75 mb-10">{page.intro}</p>
          <div className="flex flex-wrap gap-4 mb-8">
            <Link
              to="/devis"
              data-testid="seo-cta-devis"
              className="inline-flex items-center gap-3 border-2 border-ink bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper shadow-brutal transition-all hover:bg-ink hover:shadow-none"
            >
              Obtenir mon devis
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={openCallbackModal}
              data-testid="seo-cta-callback"
              className="inline-flex items-center gap-2 border-2 border-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-ink hover:text-paper"
            >
              <BellRing className="h-4 w-4" />
              Être rappelé
            </button>
            {PHONE && (
              <a
                href={telHref(PHONE)}
                onClick={() => trackEvent("phone_click", { position: "landing" })}
                data-testid="seo-cta-phone"
                className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-accent hover:border-accent"
              >
                <Phone className="h-4 w-4" />
                {PHONE}
              </a>
            )}
          </div>
          <div className="mb-20 flex flex-wrap gap-x-8 gap-y-2 border-y border-ink/15 py-4">
            {["22 métiers du bâtiment", "Rappel rapide par un spécialiste BTP", "Devis gratuit & sans engagement"].map((t) => (
              <p key={t} className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60 flex items-center gap-2">
                <span className="text-accent">◆</span>
                {t}
              </p>
            ))}
          </div>
        </Reveal>

        <div className="mb-16">
          {page.blocks &&
            page.blocks.map((b, i) => (
              <Reveal key={i}>
                <div className="grid gap-4 border-t-2 border-ink py-10 sm:grid-cols-12 sm:gap-8 items-start">
                  <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-accent sm:col-span-2">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <div className="sm:col-span-10">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-4">
                      {hy(b.h)}
                    </h2>
                    {b.paras.map((p, j) => (
                      <p key={j} className="text-base leading-relaxed text-ink/75 mb-3 max-w-3xl">
                        {p}
                      </p>
                    ))}
                    {b.list && (
                      <ul className="mt-4 space-y-2 max-w-3xl">
                        {b.list.map((item, j) => (
                          <li key={j} className="flex gap-3 text-sm leading-relaxed text-ink/75">
                            <span className="font-mono text-[10px] text-accent pt-1 shrink-0">—</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          {page.extra && (
            <Reveal>
              <div className="grid gap-4 border-t-2 border-ink py-10 sm:grid-cols-12 sm:gap-8 items-start">
                <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-accent sm:col-span-2">+</p>
                <div className="sm:col-span-10">
                  <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-4">
                    {page.extra.h}
                  </h2>
                  {page.extra.paras.map((p, j) => (
                    <p key={j} className="text-base leading-relaxed text-ink/75 mb-3 max-w-3xl">
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>

        <Reveal className="mb-16">
          <SectionTag className="mb-4">Questions fréquentes</SectionTag>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tighter mb-8">
            Questions fréquentes
          </h2>
          <Faq items={page.faq} prefix={`seo-faq-${page.slug}`} />
        </Reveal>

        <Reveal className="mb-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60 mb-6">Pages liées</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-ink/15">
            {pillarLinks.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`seo-related-${p.slug}`}
                className="border-b border-r border-ink/15 p-5 font-display text-sm font-bold uppercase tracking-tight transition-colors hover:bg-accent hover:text-paper"
              >
                {p.h1}
              </Link>
            ))}
            <Link
              to="/metiers"
              data-testid="seo-related-metiers"
              className="border-b border-r border-ink/15 p-5 font-display text-sm font-bold uppercase tracking-tight transition-colors hover:bg-ink hover:text-paper"
            >
              Assurance décennale par métier
            </Link>
            <Link
              to="/prix"
              data-testid="seo-related-prix"
              className="border-b border-r border-ink/15 p-5 font-display text-sm font-bold uppercase tracking-tight transition-colors hover:bg-ink hover:text-paper"
            >
              Prix par métier
            </Link>
          </div>
        </Reveal>
        <EditorialSources updated="2026-09-24" />
        <PracticalGuideLinks limit={3} />
      </div>

      <section className="bg-accent text-paper">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 lg:py-20">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter leading-[0.92]">
              Votre devis<br />en 2 minutes.
            </h2>
            <p className="mt-4 max-w-xl text-base text-paper">
              Gratuit, sans engagement — un conseiller spécialiste BTP vous répond rapidement.
            </p>
            <Link
              to="/devis"
              data-testid="seo-band-cta-devis"
              className="mt-10 inline-flex items-center gap-3 border-2 border-night bg-night px-10 py-5 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-transparent hover:text-night"
            >
              Obtenir mon devis
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
